import { storedRef } from "@/components/ReferralKit";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, CreditCard, Tag, Loader2, ShieldCheck, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OneDeviceNotice } from "@/components/OneDeviceNotice";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ALL_COURSES } from "@/data/curriculum";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CourseOption = ({ c, on, toggle }) => (
  <button onClick={() => toggle(c)} data-testid={`pricing-course-${c.key}`}
    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all ${on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary"}`}>
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-primary bg-primary text-white" : "border-input"}`}>
      {on && <Check size={14} />}
    </span>
    <span>
      <span className="block font-medium">{c.title}</span>
      <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-widest text-primary">{c.tagline}</span>
    </span>
  </button>
);

const SummaryLines = ({ selected, quote }) => (
  <div className="mt-5 space-y-2 text-sm">
    <div className="flex justify-between text-muted-foreground"><span>Courses selected</span><span>{selected.length}</span></div>
    {quote && (
      <>
        <div className="flex justify-between text-muted-foreground"><span>Price per course</span><span>£{quote.per_course.toFixed(0)}</span></div>
        <div className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-1.5 text-primary">
          <span className="font-medium">{quote.label}</span>
          {quote.referral_applied && <span className="text-xs">referral ✓</span>}
        </div>
        {quote.savings > 0 && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>You save</span><span>£{quote.savings.toFixed(0)}</span></div>}
      </>
    )}
    <div className="mt-2 flex items-baseline justify-between border-t border-border pt-3">
      <span className="font-serif text-lg">Total</span>
      <span className="font-serif text-3xl text-foreground" data-testid="pricing-total">£{quote ? quote.total.toFixed(0) : "0"}</span>
    </div>
  </div>
);

const BundleNudge = ({ s, toggle }) => (
  <div className="mt-5 rounded-xl border border-primary/40 bg-primary/10 p-4" data-testid="bundle-nudge">
    <p className="flex items-center gap-1.5 text-sm font-medium text-primary"><Tag size={15} /> Unlock the £850 bundle</p>
    <p className="mt-1 text-xs text-muted-foreground">Add a second course — even from another instrument — and both drop to £425 each (save £150 vs buying separately).</p>
    <button onClick={() => toggle(s)} data-testid="bundle-nudge-add" className="mt-3 flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary">
      <span className="min-w-0"><span className="block truncate font-medium">{s.title}</span><span className="text-xs text-muted-foreground">{s.tagline}</span></span>
      <span className="flex shrink-0 items-center gap-1 font-medium text-primary"><Plus size={14} /> Add</span>
    </button>
  </div>
);

// Course picker + order summary + Stripe checkout. Preselects from ?instrument=&course=.
export const CourseCheckout = () => {
  const { user, purchases } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [selected, setSelected] = useState([]);
  const [referral, setReferral] = useState((params.get("ref") || storedRef()).toUpperCase());
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const owned = new Set(purchases || []);
  const available = useMemo(() => ALL_COURSES.filter((c) => !owned.has(c.key)), [purchases]); // eslint-disable-line react-hooks/exhaustive-deps

  const bundleSuggestion = useMemo(() => {
    if (selected.length !== 1) return null;
    const notSelected = available.filter((c) => !selected.find((s) => s.key === c.key));
    return notSelected.find((c) => c.instrument_id !== selected[0].instrument_id) || notSelected[0] || null;
  }, [selected, available]);

  useEffect(() => {
    const found = ALL_COURSES.find((c) => c.instrument_id === params.get("instrument") && c.course_id === params.get("course"));
    setSelected((prev) => {
      const keep = prev.filter((c) => !owned.has(c.key));
      return found && !owned.has(found.key) && !keep.find((c) => c.key === found.key) ? [...keep, found] : keep;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchases]);

  const toggle = (c) => setSelected((prev) => (prev.find((p) => p.key === c.key) ? prev.filter((p) => p.key !== c.key) : [...prev, c]));

  useEffect(() => {
    if (selected.length === 0) { setQuote(null); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/quote", {
          courses: selected.map((c) => ({ instrument_id: c.instrument_id, course_id: c.course_id })),
          referral_code: referral || null,
        });
        if (!cancel) setQuote(data);
      } catch (e) { /* quote unavailable */ }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [selected, referral]);

  const checkout = async () => {
    if (!user) { nav("/login", { state: { from: loc.pathname + loc.search } }); return; }
    if (selected.length === 0) { toast.error("Select at least one course."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        courses: selected.map((c) => ({ instrument_id: c.instrument_id, course_id: c.course_id, title: c.title })),
        referral_code: referral || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-3" data-testid="course-checkout">
      <div className="min-w-0 lg:col-span-2">
        <h2 className="font-serif text-2xl">Choose your course(s)</h2>
        <p className="mt-1 text-sm text-muted-foreground">Select any two courses — even across different instruments — for the £850 bundle (£425 each).</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {available.map((c) => <CourseOption key={c.key} c={c} on={!!selected.find((p) => p.key === c.key)} toggle={toggle} />)}
        </div>
        {available.length === 0 && <p className="mt-4 rounded-xl bg-emerald-500/15 p-4 text-sm text-emerald-600 dark:text-emerald-400">You already own every course — bravo!</p>}
      </div>
      <div className="min-w-0 lg:col-span-1">
        <div className="sticky top-24 rounded-3xl border border-border bg-card p-6" data-testid="pricing-summary">
          <h3 className="font-serif text-xl">Order summary</h3>
          <label className="mt-5 block text-sm font-medium text-muted-foreground">Referral code (optional)</label>
          <Input value={referral} onChange={(e) => setReferral(e.target.value.toUpperCase())} placeholder="e.g. 3F9A2C1B"
            data-testid="pricing-referral-input" className="mt-1 border-border bg-background font-mono" />
          <SummaryLines selected={selected} quote={quote} />
          {bundleSuggestion && <BundleNudge s={bundleSuggestion} toggle={toggle} />}
          <OneDeviceNotice className="mt-5" testId="checkout-one-device-notice" />
          <Button onClick={checkout} disabled={loading || selected.length === 0} data-testid="pricing-checkout-button"
            className="mt-6 w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90">
            {loading ? <Loader2 className="mr-2 animate-spin" size={18} /> : <CreditCard className="mr-2" size={18} />}
            {user ? "Pay securely" : "Log in to pay"}
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"><ShieldCheck size={13} /> Card · Apple Pay · Google Pay · Link</p>
          <p className="mt-4 rounded-xl bg-secondary p-3 text-center text-xs text-muted-foreground" data-testid="pricing-referral-info">
            Refer a friend after booking: once you've booked a course, generate your referral code on your dashboard and share it. When the person you refer signs up, they save £50 (£450, or £800 for 2 courses) and you get a free 1:1 session with Jason.
          </p>
          <p className="mt-3 text-center text-xs text-muted-foreground" data-testid="checkout-keep-in-touch">
            Not quite ready? <Link to={`/keep-in-touch?from=checkout&page=${encodeURIComponent(loc.pathname)}`} className="font-semibold text-primary hover:underline" data-testid="checkout-keep-in-touch-link">Let's keep in touch</Link> and hear about offers first.
          </p>
        </div>
      </div>
    </div>
  );
};
